import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Wallet,
  CreditCard,
  TrendingUp,
  Building2,
  Receipt,
  Plus,
  ChevronRight,
  RefreshCw,
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useDataRefresh, OPEN_ADD_MODAL_EVENT } from '@/lib/events';
import EmptyState from '../components/EmptyState';
import AddItemModal from '../components/AddItemModal';
import AddTransactionModal from '../components/AddTransactionModal';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

interface HubData {
  netWorth: number;
  totalAssets: number;
  totalLiabilities: number;
  investmentsTotal: number;
  realEstateTotal: number;
  transactionsThisMonth: number;
  recentActivity: Array<{
    id: string;
    name: string;
    value: number;
    type: 'asset' | 'liability';
    action: 'added' | 'updated';
    updatedAt: string;
  }>;
}

const HUB_CARDS = [
  { to: '/dashboard/assets', label: 'Assets', icon: Wallet, key: 'totalAssets' as const },
  { to: '/dashboard/liabilities', label: 'Liabilities', icon: CreditCard, key: 'totalLiabilities' as const },
  { to: '/dashboard/investments', label: 'Investments', icon: TrendingUp, key: 'investmentsTotal' as const },
  { to: '/dashboard/real-estate', label: 'Real Estate', icon: Building2, key: 'realEstateTotal' as const },
  { to: '/dashboard/transactions', label: 'Transactions', icon: Receipt, key: 'transactionsThisMonth' as const },
];

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'today';
  if (diffDays === 1) return 'yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  return `${Math.floor(diffDays / 30)} months ago`;
}

export default function Dashboard() {
  const { profile } = useAuth();
  const [data, setData] = useState<HubData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addModalType, setAddModalType] = useState<'asset' | 'liability' | null>(null);
  const [addModalCategory, setAddModalCategory] = useState<string | undefined>(undefined);
  const [showTransactionModal, setShowTransactionModal] = useState(false);

  const firstName = profile?.full_name?.split(' ')[0] || 'there';

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: profile?.currency || 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);

  const formatTransactionCount = (count: number) =>
    count === 0 ? '0' : count === 1 ? '1 this month' : `${count} this month`;

  const fetchData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const [assetsRes, liabilitiesRes, holdingsRes, accountsRes, realEstateRes, transactionsRes] = await Promise.all([
        supabase.from('assets').select('id, name, value, created_at, updated_at').order('updated_at', { ascending: false }),
        supabase.from('liabilities').select('id, name, amount, created_at, updated_at').order('updated_at', { ascending: false }),
        supabase.from('investment_holdings').select('quantity, current_price'),
        supabase.from('investment_accounts').select('balance'),
        supabase.from('assets').select('value').eq('type', 'real_estate'),
        (async () => {
          const start = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0];
          const { count } = await supabase
            .from('transactions')
            .select('*', { count: 'exact', head: true })
            .gte('date', start);
          return count ?? 0;
        })(),
      ]);

      const assetsData = assetsRes.data || [];
      const liabilitiesData = liabilitiesRes.data || [];
      const totalAssets = assetsData.reduce((s, a) => s + Number(a.value), 0);
      const totalLiabilities = liabilitiesData.reduce((s, l) => s + Number((l as { amount: number }).amount), 0);

      const holdings = holdingsRes.data || [];
      const accounts = accountsRes.data || [];
      const investmentsTotal =
        accounts.reduce((s, a) => s + (a.balance ?? 0), 0) +
        holdings.reduce((s, h) => s + h.quantity * h.current_price, 0);

      const realEstate = realEstateRes.data || [];
      const realEstateTotal = realEstate.reduce((s, a) => s + Number(a.value), 0);

      const transactionsThisMonth = transactionsRes as number;

      const assetActivities = assetsData.map((a) => ({
        id: a.id,
        name: a.name,
        value: Number(a.value),
        type: 'asset' as const,
        action: Math.abs(new Date(a.created_at).getTime() - new Date(a.updated_at).getTime()) < 60000 ? 'added' : 'updated',
        updatedAt: a.updated_at,
      }));
      const liabilityActivities = liabilitiesData.map((l) => ({
        id: l.id,
        name: l.name,
        value: Number((l as { amount: number }).amount),
        type: 'liability' as const,
        action: Math.abs(new Date(l.created_at).getTime() - new Date(l.updated_at).getTime()) < 60000 ? 'added' : 'updated',
        updatedAt: l.updated_at,
      }));

      const recentActivity = [...assetActivities, ...liabilityActivities]
        .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
        .slice(0, 5);

      setData({
        netWorth: totalAssets - totalLiabilities,
        totalAssets,
        totalLiabilities,
        investmentsTotal,
        realEstateTotal,
        transactionsThisMonth,
        recentActivity,
      });
    } catch (err) {
      console.error('Dashboard fetch error:', err);
      setError('Failed to load your financial data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(useDataRefresh(fetchData), []);

  useEffect(() => {
    const handler = () => {
      setAddModalType(null);
      setShowAddModal(true);
    };
    window.addEventListener(OPEN_ADD_MODAL_EVENT, handler);
    return () => window.removeEventListener(OPEN_ADD_MODAL_EVENT, handler);
  }, []);

  useEffect(() => {
    if (!isLoading && !error) {
      requestAnimationFrame(() => window.scrollTo(0, 0));
    }
  }, [isLoading, error]);

  const handleAddSuccess = () => {
    setShowAddModal(false);
    setShowTransactionModal(false);
    setAddModalType(null);
    fetchData();
  };

  if (isLoading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-slate-900 border-t-transparent mx-auto" />
          <p className="mt-4 text-slate-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="text-center">
          <Card className="mb-6 border-red-200 bg-red-50">
            <CardContent className="pt-6">
              <p className="text-lg font-medium text-red-800">{error}</p>
            </CardContent>
          </Card>
          <Button onClick={fetchData} className="gap-2 rounded-full bg-slate-900 text-white hover:bg-slate-800 btn-hover-lift">
            <RefreshCw className="w-5 h-5" />
            Retry
          </Button>
        </div>
      </div>
    );
  }

  const hasAnyData =
    data.totalAssets > 0 ||
    data.totalLiabilities > 0 ||
    data.investmentsTotal > 0;

  const getHubValue = (key: keyof HubData) => {
    switch (key) {
      case 'totalAssets':
        return formatCurrency(data.totalAssets);
      case 'totalLiabilities':
        return formatCurrency(data.totalLiabilities);
      case 'investmentsTotal':
        return formatCurrency(data.investmentsTotal);
      case 'realEstateTotal':
        return formatCurrency(data.realEstateTotal);
      case 'transactionsThisMonth':
        return formatTransactionCount(data.transactionsThisMonth);
      default:
        return '0';
    }
  };

  return (
    <div className="space-y-8">
      {!data || !hasAnyData ? (
        <EmptyState
          onAddItem={(preset) => {
            setAddModalType(preset?.itemType ?? null);
            setAddModalCategory(preset?.category);
            setShowAddModal(true);
          }}
        />
      ) : (
        <>
          {/* Hero */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Hello, {firstName}</h1>
              <p className="text-lg text-slate-600 mt-0.5">
                Your net worth is {formatCurrency(data.netWorth)}
              </p>
            </div>
            <Button
              className="gap-2 shrink-0 rounded-full bg-slate-900 text-white hover:bg-slate-800 font-semibold btn-hover-lift"
              onClick={() => {
                setAddModalType(null);
                setAddModalCategory(undefined);
                setShowAddModal(true);
              }}
            >
              <Plus className="w-4 h-4" />
              Add Item
            </Button>
          </div>

          {/* Hub cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            {HUB_CARDS.map(({ to, label, icon: Icon, key }) => (
              <Link key={to} to={to}>
                <Card className="h-full rounded-xl sm:rounded-2xl border border-slate-200/80 shadow-sm transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 hover:border-slate-300/80 group cursor-pointer">
                  <CardContent className="pt-6 pb-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center">
                          <Icon className="h-5 w-5 text-slate-700" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-600">{label}</p>
                          <p className="text-lg font-semibold text-slate-900">{getHubValue(key)}</p>
                        </div>
                      </div>
                      <ChevronRight className="h-5 w-5 text-slate-500 group-hover:translate-x-0.5 transition-transform" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>

          {/* Quick actions */}
          <div>
            <h2 className="text-sm font-medium text-slate-600 mb-3">Quick actions</h2>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                className="gap-2 rounded-full border-slate-300 text-slate-700 hover:bg-slate-50 hover:border-slate-400 font-medium"
                onClick={() => {
                  setAddModalType('asset');
                  setShowAddModal(true);
                }}
              >
                <Plus className="w-4 h-4" />
                Add Asset
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="gap-2 rounded-full border-slate-300 text-slate-700 hover:bg-slate-50 hover:border-slate-400 font-medium"
                onClick={() => {
                  setAddModalType('liability');
                  setShowAddModal(true);
                }}
              >
                <Plus className="w-4 h-4" />
                Add Liability
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="gap-2 rounded-full border-slate-300 text-slate-700 hover:bg-slate-50 hover:border-slate-400 font-medium"
                onClick={() => setShowTransactionModal(true)}
              >
                <Plus className="w-4 h-4" />
                Add Transaction
              </Button>
            </div>
          </div>

          {/* Recent activity */}
          {data.recentActivity.length > 0 && (
            <div>
              <h2 className="text-sm font-medium text-slate-600 mb-3">Recent activity</h2>
              <Card className="rounded-xl sm:rounded-2xl border border-slate-200/80 shadow-sm">
                <CardContent className="p-0">
                  <ul className="divide-y divide-slate-200">
                    {data.recentActivity.map((item) => (
                      <li
                        key={`${item.type}-${item.id}`}
                        className="px-4 py-3 flex items-center justify-between text-sm"
                      >
                        <span className="text-slate-900">
                          {item.action === 'added' ? 'Added' : 'Updated'}{' '}
                          <span className="font-medium">&quot;{item.name}&quot;</span>
                          {' – '}
                          {formatCurrency(item.value)}
                        </span>
                        <span className="text-slate-500">{formatRelativeTime(item.updatedAt)}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </div>
          )}
        </>
      )}

      {/* Modals */}
      {showAddModal && (
        <AddItemModal
          onClose={() => {
            setShowAddModal(false);
            setAddModalType(null);
            setAddModalCategory(undefined);
          }}
          onSuccess={handleAddSuccess}
          initialItemType={addModalType ?? undefined}
          initialCategory={addModalCategory}
        />
      )}

      <AddTransactionModal
        open={showTransactionModal}
        onClose={() => setShowTransactionModal(false)}
        onSuccess={handleAddSuccess}
      />
    </div>
  );
}
